/**
 * Trail cosmetic rendering — shop cards, editor preview, and in-game trail.
 */
import { ANIM_FRAME_MS } from "./shop-creator-fx";
import { drawReferenceCursor, loadCosmeticImage, getCachedCosmeticImage } from "./cursor-cosmetics";
import type { TrailMode } from "../../lib/shopTypes";

export type TrailPoint = {
  x: number;
  y: number;
  life: number;
  breakAfter?: boolean;
  breakBefore?: boolean;
  animOffset?: number;
};

/** Fixed-world particle stamp (does not follow the cursor after spawn). */
export type TrailParticleStamp = {
  x: number;
  y: number;
  /** Seconds remaining. */
  life: number;
  /** Initial lifetime in seconds. */
  maxLife: number;
  /** Fixed rotation at spawn (radians). */
  rotation: number;
  /** Phase for optional frame animation. */
  animOffset: number;
};

export type TrailCosmeticVisual = {
  trailMode: TrailMode;
  trailColors?: string[] | null;
  imageData?: string | null;
  animationFrames?: string[] | null;
};

/** Minimal transparent 1×1 PNG — placeholder imageData for multiColor trails. */
export const TRAIL_PLACEHOLDER_IMAGE =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";

/** Base particle lifetime (seconds). Constant — not shortened by cursor speed. */
export const TRAIL_PARTICLE_BASE_LIFE_SEC = 1;
/** Slow frame cycle so particle art does not flicker wildly. */
const PARTICLE_FRAME_MS = Math.max(ANIM_FRAME_MS * 3, 320);

type OpaqueBox = { sx: number; sy: number; sw: number; sh: number };

const opaqueBoundsCache = new Map<string, OpaqueBox>();

export function trailModeBadgeLabel(mode: TrailMode | null | undefined): string {
  if (mode === "particle") return "Particle";
  if (mode === "multiColor") return "Gradient";
  return "";
}

/** Color at normalized distance t∈[0,1] from newest (0) to oldest (1). */
export function trailColorAt(colors: string[], t: number): string {
  if (!colors.length) return "#ffffff";
  if (colors.length === 1) return colors[0];
  const clamped = Math.max(0, Math.min(1, t));
  const idx = Math.min(colors.length - 1, Math.floor(clamped * colors.length));
  return colors[idx];
}

function pathLength(pts: Array<{ x: number; y: number }>): number {
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    total += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
  }
  return total;
}

function hash01(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

function getOpaqueBounds(img: HTMLImageElement, cacheKey: string): OpaqueBox {
  const cached = opaqueBoundsCache.get(cacheKey);
  if (cached) return cached;
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (iw <= 0 || ih <= 0) {
    const fallback = { sx: 0, sy: 0, sw: 1, sh: 1 };
    opaqueBoundsCache.set(cacheKey, fallback);
    return fallback;
  }
  const probe = document.createElement("canvas");
  probe.width = iw;
  probe.height = ih;
  const pctx = probe.getContext("2d", { willReadFrequently: true });
  if (!pctx) {
    const full = { sx: 0, sy: 0, sw: iw, sh: ih };
    opaqueBoundsCache.set(cacheKey, full);
    return full;
  }
  pctx.clearRect(0, 0, iw, ih);
  pctx.drawImage(img, 0, 0);
  let data: ImageData;
  try {
    data = pctx.getImageData(0, 0, iw, ih);
  } catch {
    const full = { sx: 0, sy: 0, sw: iw, sh: ih };
    opaqueBoundsCache.set(cacheKey, full);
    return full;
  }
  let minX = iw;
  let minY = ih;
  let maxX = -1;
  let maxY = -1;
  const px = data.data;
  for (let y = 0; y < ih; y++) {
    for (let x = 0; x < iw; x++) {
      if (px[(y * iw + x) * 4 + 3] > 8) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  const box =
    maxX < minX || maxY < minY
      ? { sx: 0, sy: 0, sw: iw, sh: ih }
      : { sx: minX, sy: minY, sw: maxX - minX + 1, sh: maxY - minY + 1 };
  opaqueBoundsCache.set(cacheKey, box);
  return box;
}

/** How much faster lifetime burns based on cursor speed (px/s). */
export function trailParticleSpeedDecayMul(_speedPxPerSec: number): number {
  // Lifetime is constant; speed only gates spawning (must be moving).
  return 1;
}

export function trailParticleMaxLifeSec(_speedPxPerSec?: number): number {
  return TRAIL_PARTICLE_BASE_LIFE_SEC;
}

function pickStampRotation(seed: number): number {
  const bucket = Math.floor(hash01(seed) * 8);
  if (bucket < 4) return (bucket * Math.PI) / 2;
  return hash01(seed + 19) * Math.PI * 2;
}

/**
 * Spawn a stamp when the cursor has moved far enough from the last spawn.
 * Multiple stamps can coexist; each lives a fixed ~1s (speed does not shorten life).
 */
export function trySpawnTrailParticleStamp(
  stamps: TrailParticleStamp[],
  x: number,
  y: number,
  _speedPxPerSec?: number,
  opts?: { minSpacing?: number; maxStamps?: number },
): TrailParticleStamp[] {
  const minSpacing = opts?.minSpacing ?? 28;
  const maxStamps = opts?.maxStamps ?? 16;
  const last = stamps.length ? stamps[stamps.length - 1] : null;
  if (last) {
    const dist = Math.hypot(x - last.x, y - last.y);
    if (dist < minSpacing) return stamps;
  }
  const maxLife = TRAIL_PARTICLE_BASE_LIFE_SEC;
  const seed = Math.floor(x * 13 + y * 17 + performance.now());
  const next = [
    ...stamps,
    {
      x,
      y,
      life: maxLife,
      maxLife,
      rotation: pickStampRotation(seed),
      animOffset: hash01(seed + 7) * 1000,
    },
  ];
  while (next.length > maxStamps) next.shift();
  return next;
}

/** Decay stamps at a constant rate. Positions stay fixed. */
export function updateTrailParticleStamps(
  stamps: TrailParticleStamp[],
  dtSec: number,
  _speedPxPerSec?: number,
): TrailParticleStamp[] {
  if (!stamps.length) return stamps;
  const next: TrailParticleStamp[] = [];
  for (const s of stamps) {
    const life = s.life - dtSec;
    if (life > 0) next.push({ ...s, life });
  }
  return next;
}

export async function paintParticleStamps(
  ctx: CanvasRenderingContext2D,
  stamps: TrailParticleStamp[],
  visual: TrailCosmeticVisual,
  opts?: {
    particleSize?: number;
    nowMs?: number;
    alphaScale?: number;
  },
) {
  const frames =
    visual.animationFrames?.filter((f) => typeof f === "string" && f.length > 0) ?? [];
  const src = frames[0] || visual.imageData;
  if (!src || !stamps.length) return;

  await loadCosmeticImage(src);
  if (frames.length) {
    await Promise.all(frames.map((f) => loadCosmeticImage(f)));
  }

  const size = opts?.particleSize ?? 36;
  const nowMs = opts?.nowMs ?? performance.now();
  const alphaScale = opts?.alphaScale ?? 0.95;

  ctx.save();
  ctx.imageSmoothingEnabled = false;
  for (const stamp of stamps) {
    const alpha = Math.max(0, stamp.life / stamp.maxLife) * alphaScale;
    if (alpha <= 0.01) continue;

    let frameSrc = src;
    if (frames.length > 1) {
      const frameIdx = Math.floor((nowMs + stamp.animOffset) / PARTICLE_FRAME_MS) % frames.length;
      frameSrc = frames[frameIdx];
    }
    const img = getCachedCosmeticImage(frameSrc);
    if (!img) continue;

    const box = getOpaqueBounds(img, frameSrc);
    const contentMax = Math.max(box.sw, box.sh, 1);
    const drawSize = size * 0.92;
    const scale = drawSize / contentMax;
    const dw = box.sw * scale;
    const dh = box.sh * scale;

    ctx.save();
    ctx.translate(stamp.x, stamp.y);
    ctx.rotate(stamp.rotation);
    ctx.globalAlpha = alpha;
    ctx.drawImage(img, box.sx, box.sy, box.sw, box.sh, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }
  ctx.restore();
}

/** @deprecated Prefer paintParticleStamps with fixed TrailParticleStamp[]. */
export async function paintParticleTrailStroke(
  ctx: CanvasRenderingContext2D,
  points: TrailPoint[],
  visual: TrailCosmeticVisual,
  opts?: {
    maxLife?: number;
    particleSize?: number;
    spacing?: number;
    nowMs?: number;
    alphaScale?: number;
  },
) {
  if (points.length < 1) return;
  const stamps: TrailParticleStamp[] = points.map((p, i) => ({
    x: p.x,
    y: p.y,
    life: p.life,
    maxLife: opts?.maxLife ?? 12,
    rotation: pickStampRotation(i * 97 + Math.floor(p.animOffset ?? 0)),
    animOffset: p.animOffset ?? i * 41,
  }));
  await paintParticleStamps(ctx, stamps, visual, opts);
}

/** S-curve path for idle shop/inventory cards (no cursor). */
export function buildTrailCardCurve(
  w: number,
  h: number,
  samples = 48,
): Array<{ x: number; y: number }> {
  const padX = w * 0.12;
  const padY = h * 0.22;
  const pts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < samples; i++) {
    const u = i / (samples - 1);
    const x = padX + u * (w - padX * 2);
    const y = h * 0.5 + Math.sin(u * Math.PI * 2) * (h * 0.5 - padY);
    pts.push({ x, y });
  }
  return pts;
}

/** Looping path for animated trail previews (figure-8 / zigzag). */
export function trailPreviewCursorPos(tSec: number, w: number, h: number): { x: number; y: number } {
  const cx = w * 0.5;
  const cy = h * 0.5;
  const rx = w * 0.32;
  const ry = h * 0.28;
  const a = tSec * 1.35;
  return {
    x: cx + Math.sin(a) * rx,
    y: cy + Math.sin(a * 2) * ry * 0.85,
  };
}

export function paintMultiColorTrailStroke(
  ctx: CanvasRenderingContext2D,
  points: Array<{ x: number; y: number; life?: number; breakAfter?: boolean; breakBefore?: boolean }>,
  colors: string[],
  opts?: { maxLife?: number; lineWidth?: number; alphaScale?: number },
) {
  if (points.length < 2 || !colors.length) return;
  if (pathLength(points) < 2) return;
  const maxLife = opts?.maxLife ?? 12;
  const baseW = opts?.lineWidth ?? 4;
  const alphaScale = opts?.alphaScale ?? 0.45;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const n = points.length - 1;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (a.breakAfter || b.breakBefore) continue;
    const seg = Math.hypot(b.x - a.x, b.y - a.y);
    if (seg < 0.75) continue;
    const t = n <= 1 ? 0 : i / n;
    const life = a.life ?? maxLife;
    const alpha = Math.max(0, life / maxLife) * alphaScale;
    ctx.strokeStyle = trailColorAt(colors, t);
    ctx.globalAlpha = alpha;
    ctx.lineWidth = Math.max(1, (life / maxLife) * baseW);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
  ctx.restore();
}

/** Idle card: curved trail only (no cursor). */
export async function paintTrailCardIdle(
  canvas: HTMLCanvasElement,
  visual: TrailCosmeticVisual,
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const curve = buildTrailCardCurve(w, h);
  if (visual.trailMode === "multiColor") {
    const colors = visual.trailColors?.length ? visual.trailColors : ["#ff66ff", "#66ffff"];
    const pts = curve.map((p, i) => ({
      ...p,
      life: 12 * (1 - i / Math.max(1, curve.length - 1)) * 0.85 + 2,
    }));
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    paintMultiColorTrailStroke(ctx, pts, colors, {
      maxLife: 12,
      lineWidth: Math.max(3, Math.min(w, h) * 0.07),
      alphaScale: 0.7,
    });
    ctx.restore();
    return;
  }
  // Static preview: a few spaced stamps with fixed rotations (not a moving swarm).
  const particleSize = Math.max(28, Math.min(w, h) * 0.28);
  const stamps: TrailParticleStamp[] = [];
  const step = Math.max(1, Math.floor(curve.length / 5));
  for (let i = 0; i < curve.length; i += step) {
    const p = curve[i];
    stamps.push({
      x: p.x,
      y: p.y,
      life: 1,
      maxLife: 1,
      rotation: pickStampRotation(i * 31),
      animOffset: i * 40,
    });
  }
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  await paintParticleStamps(ctx, stamps, visual, { particleSize, alphaScale: 0.95 });
  ctx.restore();
}

export type TrailPreviewState = {
  lineHist: TrailPoint[];
  particleStamps: TrailParticleStamp[];
  lastPos: { x: number; y: number } | null;
  lastTSec: number;
};

export function createTrailPreviewState(): TrailPreviewState {
  return { lineHist: [], particleStamps: [], lastPos: null, lastTSec: 0 };
}

/** Hover / editor: moving cursor with live trail. */
export async function paintTrailCardPreview(
  canvas: HTMLCanvasElement,
  visual: TrailCosmeticVisual,
  tSec: number,
  cursorColor = "#ffffff",
  state?: TrailPreviewState | TrailPoint[],
): Promise<TrailPreviewState> {
  const ctx = canvas.getContext("2d");
  const empty = createTrailPreviewState();
  if (!ctx) return empty;
  const w = canvas.width;
  const h = canvas.height;
  const pos = trailPreviewCursorPos(tSec, w, h);

  // Back-compat: older callers passed TrailPoint[] history.
  const st: TrailPreviewState = Array.isArray(state)
    ? { lineHist: state, particleStamps: [], lastPos: null, lastTSec: tSec }
    : state
      ? state
      : createTrailPreviewState();

  const dtSec = st.lastTSec > 0 ? Math.max(0.001, Math.min(0.08, tSec - st.lastTSec)) : 1 / 60;
  let speed = 0;
  if (st.lastPos) {
    speed = Math.hypot(pos.x - st.lastPos.x, pos.y - st.lastPos.y) / dtSec;
  }
  const moving = speed > 18;

  ctx.clearRect(0, 0, w, h);
  ctx.save();
  ctx.globalCompositeOperation = "lighter";

  if (visual.trailMode === "multiColor") {
    const minDist = Math.max(3, Math.min(w, h) * 0.02);
    let next = [...st.lineHist].map((p) => ({ ...p, life: p.life - 0.35 }));
    const head = next[0];
    if (moving && (!head || Math.hypot(pos.x - head.x, pos.y - head.y) >= minDist)) {
      next = [{ x: pos.x, y: pos.y, life: 12, animOffset: performance.now() % 10000 }, ...next];
    }
    next = next.filter((p) => p.life > 0);
    while (next.length > 28) next.pop();
    const colors = visual.trailColors?.length ? visual.trailColors : ["#ff66ff"];
    paintMultiColorTrailStroke(ctx, next, colors, {
      maxLife: 12,
      lineWidth: Math.max(2.5, Math.min(w, h) * 0.055),
      alphaScale: 0.55,
    });
    st.lineHist = next;
    st.particleStamps = [];
  } else {
    let stamps = updateTrailParticleStamps(st.particleStamps, dtSec, speed);
    if (moving) {
      const particleSize = Math.max(24, Math.min(w, h) * 0.24);
      stamps = trySpawnTrailParticleStamp(stamps, pos.x, pos.y, speed, {
        minSpacing: Math.max(18, particleSize * 0.85),
        maxStamps: 12,
      });
    }
    const particleSize = Math.max(24, Math.min(w, h) * 0.24);
    await paintParticleStamps(ctx, stamps, visual, { particleSize, alphaScale: 0.95 });
    st.particleStamps = stamps;
    st.lineHist = [];
  }
  ctx.restore();

  const r = Math.max(4, Math.min(w, h) * 0.07);
  drawReferenceCursor(ctx, pos.x, pos.y, r, cursorColor);
  st.lastPos = { x: pos.x, y: pos.y };
  st.lastTSec = tSec;
  return st;
}

/** Build a small gradient PNG data URL for multiColor publish placeholder. */
export function buildMultiColorTrailPreviewDataUrl(colors: string[]): string {
  const c = document.createElement("canvas");
  c.width = 128;
  c.height = 128;
  const ctx = c.getContext("2d");
  if (!ctx) return TRAIL_PLACEHOLDER_IMAGE;
  const cols = colors.length ? colors : ["#ffffff"];
  const curve = buildTrailCardCurve(128, 128, 64);
  const pts = curve.map((p, i) => ({
    ...p,
    life: 12 * (1 - i / 63) * 0.85 + 2,
  }));
  ctx.clearRect(0, 0, 128, 128);
  paintMultiColorTrailStroke(ctx, pts, cols, { maxLife: 12, lineWidth: 10, alphaScale: 0.85 });
  return c.toDataURL("image/png");
}
